import {
  X, Save 
} from 'lucide-react';
import { Button } from './button';

interface FormModalProps {
  readonly title: string;
  readonly description?: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly saveLabel?: string;
  readonly saveDisabled?: boolean;
  readonly children: React.ReactNode;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
  readonly hideCancelButton?: boolean;
  readonly hideSaveButton?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export function FormModal({
  title,
  description,
  isOpen,
  onClose,
  onSave,
  saveLabel = 'Save',
  saveDisabled = false,
  children,
  size = 'lg',
  hideCancelButton = false,
  hideSaveButton = false,
}: FormModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`w-full ${sizeClasses[size]} max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-xl`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {children}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          {!hideCancelButton && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          {!hideSaveButton && (
            <Button onClick={onSave} disabled={saveDisabled}>
              <Save className="mr-2 h-4 w-4" />
              {saveLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface FormFieldProps {
  readonly label: string;
  readonly required?: boolean;
  readonly error?: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function FormField({
  label, required, error, children, className = ''
}: FormFieldProps) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="text-sm font-medium">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  readonly error?: boolean;
  readonly icon?: React.ReactNode;
}

export function FormInput({
  error, icon, className = '', ...props 
}: Readonly<FormInputProps>) {
  if (icon) {
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        <input
          className={`w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
            error ? 'border-red-500' : 'border-input'
          } ${className}`}
          {...props}
        />
      </div>
    );
  }

  return (
    <input
      className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
        error ? 'border-red-500' : 'border-input'
      } ${className}`}
      {...props}
    />
  );
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  readonly error?: boolean;
  readonly options: {
    value: string;
    label: string;
  }[];
  readonly placeholder?: string;
}

export function FormSelect({
  error, options, placeholder, className = '', ...props 
}: Readonly<FormSelectProps>) {
  return (
    <select
      className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
        error ? 'border-red-500' : 'border-input'
      } ${className}`}
      {...props}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {readonly error?: boolean;}

export function FormTextarea({
  error, className = '', ...props 
}: Readonly<FormTextareaProps>) {
  return (
    <textarea
      className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
        error ? 'border-red-500' : 'border-input'
      } ${className}`}
      {...props}
    />
  );
}
